import { Popover as HUIPopover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Fragment, memo } from 'react';

import { useDeleteCustomScheduleExpectation } from '@/hooks/useScheduleExpectations';
import EndDateSelection from '@/pages/Scheduling/components/EndDateSelection';
import { ChronologicalScheduleExpectation } from '@/types/scheduleExpectation';
import { Button as ButtonV2 } from '@/ui';

import ScheduleExpectationDetails from './ScheduleExpectationDetails';

interface Props {
  item: ChronologicalScheduleExpectation;
  activeExpectationId?: string | null;
  phaseId: string;
}

const ScheduleExpectationItemDetail = ({ item, activeExpectationId, phaseId }: Props) => {
  const { expectations, object_type } = item;

  const isCustom = object_type === 'CustomScheduleExpectation';
  const hasEndDate = !!item.end_at;
  const expectationId = item.object_id;

  const { mutateAsync: deleteExpectation, isPending: isDeleting } =
    useDeleteCustomScheduleExpectation(phaseId);

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this custom schedule expectation?')) return;
    await deleteExpectation({ id: expectationId });
  };

  return (
    <div>
      <div className="mt-2 flex flex-col gap-y-2">
        {expectations.map((expectation, index) => {
          const expectationMatchId =
            expectation.custom_schedule_expectation_id || expectation.schedule_expectation_id;
          const isExpectationActive =
            !!activeExpectationId &&
            !!expectationMatchId &&
            expectationMatchId === activeExpectationId;
          return (
            <ScheduleExpectationDetails
              key={
                expectation.custom_schedule_expectation_id ||
                expectation.schedule_expectation_id ||
                index
              }
              expectation={expectation}
              isActive={isExpectationActive}
            />
          );
        })}
      </div>

      {isCustom && (
        <div className="flex space-x-2 mt-2">
          {!hasEndDate && (
            <HUIPopover className="relative">
              <PopoverButton as={Fragment}>
                <ButtonV2 variant="subtle" color="warning" size="xs">
                  End
                </ButtonV2>
              </PopoverButton>
              <PopoverPanel className="absolute left-0 z-20 mt-1 w-72 rounded-lg border border-uniform-gray-200 dark:border-uniform-gray-700 bg-white dark:bg-uniform-gray-900 shadow-lg p-3">
                <EndDateSelection customScheduleExpectationId={expectationId} phaseId={phaseId} />
              </PopoverPanel>
            </HUIPopover>
          )}

          <ButtonV2
            variant="default"
            color="destructive"
            size="xs"
            disabled={isDeleting}
            onClick={handleDelete}>
            Delete
          </ButtonV2>
        </div>
      )}
    </div>
  );
};

export default memo(ScheduleExpectationItemDetail);
